import {OverlayRef, ScrollStrategy} from "@angular/cdk/overlay";
import {ScrollDispatcher} from "@angular/cdk/scrolling";
import {Subscription} from "rxjs";

/** Scroll container the trigger row lives in. */
const TABLE_VIEWPORT_SELECTOR = 'cdk-virtual-scroll-viewport, .table-scroll';
/** Header cell pinned at the top of that container. */
const STICKY_HEADER_SELECTOR = 'th.table-header, [role="columnheader"]';
/** Assumed sticky-header height when the table renders no header cell. */
const ASSUMED_HEADER_HEIGHT = 44;
/** Slack around the menu so the arrow, border and drop shadow are never clipped. */
const CLIP_BLEED = 50;

/**
 * Repositions the actions menu while its row scrolls inside the table, clipping the
 * part that slides under the sticky header. Closes the menu once the row is
 * virtualized out of the DOM, since there is nothing left to anchor to.
 */
export class BoundaryRepositionScrollStrategy implements ScrollStrategy {
    private overlayRef: OverlayRef | null = null;
    private scrollSub: Subscription | null = null;
    private viewport: HTMLElement | null = null;
    private header: HTMLElement | null = null;

    constructor(
        private scrollDispatcher: ScrollDispatcher,
        private getTrigger: () => HTMLElement | null,
        private onClose: () => void
    ) {}

    attach(overlayRef: OverlayRef): void {
        this.overlayRef = overlayRef;
    }

    enable(): void {
        if (this.scrollSub) {
            return;
        }
        this.scrollSub = this.scrollDispatcher.scrolled(0).subscribe(() => this.onScroll());
    }

    disable(): void {
        this.scrollSub?.unsubscribe();
        this.scrollSub = null;
        this.viewport = null;
        this.header = null;
        if (this.overlayRef?.overlayElement) {
            this.overlayRef.overlayElement.style.clipPath = '';
            this.overlayRef.overlayElement.style.display = '';
        }
    }

    detach(): void {
        this.disable();
        this.overlayRef = null;
    }

    private onScroll(): void {
        const overlayEl = this.overlayRef?.overlayElement;
        if (!overlayEl) {
            return;
        }

        const trigger = this.getTrigger();
        if (!trigger || !document.contains(trigger)) {
            // The row was virtualized away, so the menu has no anchor left to follow.
            overlayEl.style.display = 'none';
            this.onClose();
            return;
        }

        this.overlayRef.updatePosition();

        const headerBottom = this.getStickyHeaderBottom(trigger);
        if (headerBottom === null) {
            // Not inside a table viewport, so there is no sticky header to clip against.
            overlayEl.style.display = '';
            overlayEl.style.clipPath = '';
            return;
        }

        const viewportRect = this.viewport.getBoundingClientRect();
        const row = trigger.closest('tr, [role="row"], .fd-table__row') || trigger;
        const rowRect = row.getBoundingClientRect();
        const menuRect = overlayEl.getBoundingClientRect();
        const topClip = Math.max(0, Math.ceil(headerBottom - menuRect.top));

        const isRowOutOfView = (rowRect.height > 0 && rowRect.bottom <= headerBottom) ||
            (rowRect.height > 0 && rowRect.top >= viewportRect.bottom);
        const isMenuSubmerged = menuRect.height > 0 && topClip >= menuRect.height;
        const isMenuOutOfView = isMenuSubmerged || (menuRect.height > 0 && menuRect.top >= viewportRect.bottom);

        if (isRowOutOfView || isMenuOutOfView) {
            // Completely submerged or scrolled out of table bounds. Close menu to avoid dangling focus trap.
            overlayEl.style.display = 'none';
            overlayEl.style.clipPath = '';
            this.onClose();
            return;
        }

        overlayEl.style.display = '';
        overlayEl.style.clipPath = topClip > 0
            ? `inset(${topClip}px ${-CLIP_BLEED}px ${-CLIP_BLEED}px ${-CLIP_BLEED}px)`
            : '';
    }

    /**
     * Bottom edge of the sticky header the menu has to stay below, or null when the
     * trigger is not inside a table. Lookups are cached until the elements detach.
     */
    private getStickyHeaderBottom(trigger: HTMLElement): number | null {
        if (!this.viewport || !document.contains(this.viewport)) {
            this.viewport = trigger.closest<HTMLElement>(TABLE_VIEWPORT_SELECTOR);
            this.header = null;
        }
        if (!this.viewport) {
            return null;
        }
        if (!this.header || !document.contains(this.header)) {
            this.header = this.viewport.querySelector<HTMLElement>(STICKY_HEADER_SELECTOR);
        }
        return this.header
            ? this.header.getBoundingClientRect().bottom
            : this.viewport.getBoundingClientRect().top + ASSUMED_HEADER_HEIGHT;
    }
}
